import { MailOutlined } from '@ant-design/icons';
import {
  EventNotificationFrequencyRealTime,
  EventNotificationFrequencyWeekly,
  WaterbodyDetail,
} from '@omniafishing/core';
import { Checkbox, Form, Input, Select } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useQueryString } from '../../../../hooks/use_query_string';
import { useResponsive } from '../../../../hooks/use_responsive';
import { useRudderstack } from '../../../../hooks/use_rudderstack';
import { useUserWaterbodies } from '../../../../hooks/use_user_waterbodies';
import { apiV1 } from '../../../../lib/api';
import { getIp } from '../../../../redux/geographic_location';
import { RoutePaths } from '../../../../routes';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import { OmniaLinkButton } from '../../../omnia_button/omnia_link_button';
import { WaterbodyDetailQueryParams } from '../../waterbody_detail';
import styles from './waterbody_detail_follow_form.less';
import { FollowLakeFormValues } from './waterbody_detail_follow_modal';

interface WaterbodyFollowFormLoggedOutProps {
  waterbody: WaterbodyDetail;
  onSubmitClick: () => void;
}

const CURRENT_YEAR = new Date().getFullYear();

export const WaterbodyFollowFormLoggedOut = (props: WaterbodyFollowFormLoggedOutProps) => {
  const { waterbody, onSubmitClick } = props;
  const { anonymousId } = useRudderstack();
  const ipAddress = useSelector(getIp);
  const { getCurrentQuery, replaceQueryString } = useQueryString();
  const currentQuery = getCurrentQuery<WaterbodyDetailQueryParams>();
  const { favoriteWaterbody } = useUserWaterbodies();
  const { isDesktop, isMobile } = useResponsive();
  const isFollowing = currentQuery.following;

  const formFields = {
    email: 'email',
    frequency: 'frequency',
    newsletter_opt_in: 'newsletter_opt_in',
  };

  const onFinish = (values: FollowLakeFormValues) => {
    const { email, frequency, newsletter_opt_in } = values;
    const event_notifications = {
      new_fishing_report: true,
      new_article: true,
      frequency,
    };

    if (newsletter_opt_in) {
      apiV1.newsletterSubscribe({
        email: values.email,
        anonymous_id: anonymousId,
        user_agent: window.navigator.userAgent,
        ip_address: ipAddress,
        location: 'Lake Page Fishing Reports Follow Section',
      });
    }
    favoriteWaterbody.mutate(
      {
        event_notifications,
        email,
        url_slug: waterbody.url_slug,
      },
      { onSuccess: () => replaceQueryString({ following: true, email }) }
    );
  };

  const { Option } = Select;

  const frequencySelector = (
    <Form.Item name={[formFields.frequency]} noStyle>
      <Select>
        <Option value={EventNotificationFrequencyWeekly}>Weekly</Option>
        <Option value={EventNotificationFrequencyRealTime}>Realtime</Option>
      </Select>
    </Form.Item>
  );

  return (
    <div className={styles.contentContainer}>
      {!isFollowing ? (
        <div>
          <p className={styles.followMessage}>
            Subscribe to Email Updates for the Latest {CURRENT_YEAR} {waterbody.primary_name}{' '}
            Fishing Reports
          </p>
          <div className={styles.formContainer}>
            <Form
              initialValues={{
                [formFields.newsletter_opt_in]: false,
                [formFields.frequency]: EventNotificationFrequencyWeekly,
              }}
              layout="vertical"
              onFinish={onFinish}
            >
              <div className={styles.inlineFormInputs}>
                <Form.Item
                  name={formFields.email}
                  className={styles.formItem}
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ]}
                >
                  <Input
                    type="email"
                    prefix={<MailOutlined className="site-form-item-icon" />}
                    placeholder="Email Address"
                    addonAfter={frequencySelector}
                  />
                </Form.Item>
                {isDesktop && (
                  <OmniaButton
                    kind="primary"
                    size="lg"
                    type="submit"
                    onPress={() => onSubmitClick()}
                    fontSize={14}
                    className={styles.notifyMeButton}
                  >
                    Notify Me
                  </OmniaButton>
                )}
              </div>
              <Form.Item
                name={formFields.newsletter_opt_in}
                valuePropName="checked"
                className={styles.checkbox}
              >
                <Checkbox>
                  Also sign me up for tips and deals from the Omnia newsletter.{' '}
                  <Link to={RoutePaths.NEWSLETTER} target="_blank">
                    Learn more.
                  </Link>
                </Checkbox>
              </Form.Item>
              {isMobile && (
                <OmniaButton
                  kind="primary"
                  size="lg"
                  type="submit"
                  onPress={() => onSubmitClick()}
                  className={styles.center}
                  fontSize={14}
                >
                  Notify Me
                </OmniaButton>
              )}
            </Form>
          </div>
        </div>
      ) : (
        <div className={styles.followFormNextStep}>
          <p className={styles.followMessage}>
            Thank You for Subscribing to Email Updates for {waterbody.primary_name} Fishing Reports
            <span>
              We will send updates to the email address you provided. In the meantime, create an
              account to set your preferences and personalize your experience.
            </span>
          </p>
          <div>
            <OmniaLinkButton
              to={{ pathname: RoutePaths.SIGNUP, search: `?email=${currentQuery.email}` }}
              kind="primary"
              size="lg"
              fontSize={14}
            >
              Create My Account
            </OmniaLinkButton>
          </div>
        </div>
      )}
    </div>
  );
};
